import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, Folder, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { useCategoryStore } from '../../hooks';
import { Button, Card, CardContent, useToast } from '@lancar-pos/ui';

export function CategoriesPage() {
  const navigate = useNavigate();
  const { categories, deleteCategory } = useCategoryStore();
  const toast = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<typeof categories[0] | null>(null);

  const filteredCategories = useMemo(() => {
    return categories
      .filter((c) => !c._deleted)
      .filter((c) => {
        if (!searchQuery) return true;
        return c.name.toLowerCase().includes(searchQuery.toLowerCase());
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [categories, searchQuery]);

  const handleDelete = (category: typeof categories[0]) => {
    setCategoryToDelete(category);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    
    setDeletingId(categoryToDelete.id);
    try {
      await deleteCategory(categoryToDelete.id);
      toast.success('Kategori dihapus');
    } catch (error) {
      toast.error('Gagal menghapus kategori');
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    }
  };

  const getParentName = (parentId: string | null) => {
    if (!parentId) return '-';
    const parent = categories.find((c) => c.id === parentId);
    return parent?.name || '-';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Kategori Produk</h1>
          </div>
          <Button onClick={() => navigate({ to: '/app/inventory/categories/$categoryId', params: { categoryId: 'new' } })}>
            <Plus className="w-4 h-4 mr-2" />
            Tambah Kategori
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari kategori..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kategori
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Induk
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Urutan
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCategories.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        <Folder className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Belum ada kategori</p>
                        <p className="text-sm text-gray-400">
                          {searchQuery ? 'Coba kata kunci lain' : 'Tambah kategori pertama Anda'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredCategories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                              style={{ backgroundColor: category.color + '20', color: category.color }}
                            >
                              {category.icon || '📁'}
                            </div>
                            <span className="font-medium text-gray-900">{category.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {getParentName(category.parentId)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {category.sortOrder}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() =>
                                navigate({ to: '/app/inventory/categories/$categoryId', params: { categoryId: category.id } })
                              }
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(category)}
                              disabled={deletingId === category.id}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {showDeleteDialog && categoryToDelete && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowDeleteDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Hapus Kategori?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Kategori "{categoryToDelete.name}" akan dihapus. Produk dalam kategori ini tidak akan terhapus.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deletingId === categoryToDelete.id}>
                Batal
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deletingId === categoryToDelete.id}>
                Ya, Hapus
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
